import { useState } from 'react';
import type { Cycle, PlanningBatch, PlanningResource } from '../../api/types';
import { ApiErrorMessage, Modal, SelectField } from '../referentiel/ResourcesPanel';

/** Convertit un Date en valeur d'input datetime-local (UTC, sans secondes). */
function toLocalInput(iso: string): string {
  return iso.slice(0, 16);
}
function fromLocalInput(value: string): string {
  // On interprete la saisie comme de l'heure UTC (le planning raisonne en UTC).
  return new Date(`${value}:00Z`).toISOString();
}

export interface BatchDraft {
  id?: number;
  resourceId: number;
  cycleId: number;
  t0: string;
}

/** Creation ou deplacement d'une fournee. Filtre les moyens compatibles du cycle. */
export default function BatchFormModal({
  draft,
  resources,
  cycles,
  saving,
  error,
  onClose,
  onSubmit,
  onDelete,
}: {
  draft: BatchDraft;
  resources: PlanningResource[];
  cycles: Cycle[];
  saving: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: (data: BatchDraft) => void;
  onDelete?: () => void;
}) {
  const [form, setForm] = useState<BatchDraft>(draft);
  const set = <K extends keyof BatchDraft>(k: K, v: BatchDraft[K]) => setForm((f) => ({ ...f, [k]: v }));

  const selectedCycle = cycles.find((c) => c.id === form.cycleId);
  const allowedResourceIds = selectedCycle?.allowedResources?.map((a) => a.resourceId) ?? [];
  // Un cycle limite les moyens ; on n'offre que les compatibles pour eviter un
  // refus serveur previsible.
  const eligibleResources = selectedCycle ? resources.filter((r) => allowedResourceIds.includes(r.id)) : resources;

  return (
    <Modal title={draft.id ? `Fournée #${draft.id}` : 'Nouvelle fournée'} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(form);
        }}
        className="space-y-4"
      >
        <SelectField
          label="Cycle"
          value={String(form.cycleId || '')}
          onChange={(v) => {
            const cid = Number(v);
            const cyc = cycles.find((c) => c.id === cid);
            const allowed = cyc?.allowedResources?.map((a) => a.resourceId) ?? [];
            // Si le moyen courant n'est plus compatible, on prend le premier eligible.
            setForm((f) => ({ ...f, cycleId: cid, resourceId: allowed.includes(f.resourceId) ? f.resourceId : (allowed[0] ?? f.resourceId) }));
          }}
          options={[['', '— choisir —'], ...cycles.map((c) => [String(c.id), `${c.code} — ${c.label}`] as [string, string])]}
        />

        <SelectField
          label="Moyen chaud"
          value={String(form.resourceId || '')}
          onChange={(v) => set('resourceId', Number(v))}
          options={[['', '— choisir —'], ...eligibleResources.map((r) => [String(r.id), r.label] as [string, string])]}
        />

        <label className="block">
          <span className="text-sm text-gray-600">Début du chargement</span>
          <input
            type="datetime-local"
            className="mt-1 w-full border rounded px-3 py-2"
            value={toLocalInput(form.t0)}
            onChange={(e) => set('t0', fromLocalInput(e.target.value))}
            required
          />
          <span className="text-xs text-gray-400">Le moyen est réservé jusqu'à la fin du déchargement (durée maximum du cycle).</span>
        </label>

        <ApiErrorMessage error={error} />

        <div className="flex justify-between items-center pt-2">
          {onDelete ? (
            <button type="button" className="text-red-600 hover:text-red-800 text-sm" onClick={onDelete}>
              Supprimer
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button type="button" className="px-4 py-2 rounded border" onClick={onClose}>
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              disabled={saving || !form.cycleId || !form.resourceId}
            >
              {saving ? 'Enregistrement…' : draft.id ? 'Déplacer' : 'Créer'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

/** Petit resume horaire d'une fournee, reutilisable. */
export function batchTimeLabel(batch: PlanningBatch): string {
  const fmt = (iso: string) => new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  const uncertain = batch.t2min !== batch.t2max;
  return `${fmt(batch.t0)} → ${fmt(batch.t3)}${uncertain ? ' (fin incertaine)' : ''}`;
}
